import React, { useCallback, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { negate } from 'lodash';

import IndexedInput from 'components/form/IndexedInput.component';
import getAnyOrVoid from 'utils/accessors/getAnyOrVoid.util';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToInteger from 'utils/converters/fromAnyToInteger';
import focusElement from 'utils/dom/focusElement.util';
import logger from 'utils/logger.util';
import deRefAt from 'utils/objects/deRefAt.util';
import isFn from 'utils/predicates/function/isFn.util';

const L = logger('OldCodeInput');

const KEY_BACKSPACE = 'Backspace';
const KEY_DELETE = 'Delete';
const MAX_INDEX = 5;
const LENGTH = MAX_INDEX + 1;

const nullArray = (length) => Array(length).fill(null);
const fromEventToDigit = (event) => {
  const int = fromAnyToInteger(getAnyOrVoid('target.value', event));

  if (Number.isSafeInteger(int)) {
    // if someone inputs two digits, cut it down to the last
    return int % 10;
  }

  return null;
};

const isGoodDigit = (digit) => Number.isSafeInteger(digit) && digit >= 0 && digit <= 9;
const lastBadIndex = (code) => code.findIndex(negate(isGoodDigit));

const addDigit = ({ code, index, digit }) => {
  const newCode = [...code];
  newCode[index] = isGoodDigit(digit) ? digit : null;
  return newCode;
};

const delDigit = ({ code, index }) => {
  const newCode = [...code];
  for (let i = index; i < newCode.length; i += 1) {
    newCode[i] = null;
  }
  return newCode;
};

// DEPRECATED: use CodeInput through FormField
const OldCodeInput = ({ label, type, onValidate, onBlur }) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [code, setCode] = useState(nullArray(LENGTH));
  const inputsRef = useRef(nullArray(LENGTH));

  const isComplete = lastBadIndex(code) < 0;

  // eslint-disable-next-line no-return-assign
  const setRefFor = useCallback((index) => (el) => void (inputsRef.current[index] = el), []);

  const handleChange = useCallback(
    (index, event) => {
      const digit = fromEventToDigit(event);

      if (isGoodDigit(digit) && index < MAX_INDEX) {
        focusElement(deRefAt(index + 1, inputsRef));
      }

      const newCode = addDigit({ code, index, digit });

      L.debug('handleChange()', { index, digit, isComplete, code, newCode });
      setCode(newCode);

      if (isFn(onValidate)) {
        setErrorMessage(onValidate(newCode));
      }
    },
    [code, isComplete, onValidate],
  );

  const handleKeyDown = useCallback(
    (index, event) => {
      const { key } = event;

      if (key === KEY_BACKSPACE || key === KEY_DELETE) {
        const newCode = delDigit({ code, index });

        L.debug('handleKeyDown()', { index, isComplete, code, newCode });
        setCode(newCode);

        if (isFn(onValidate)) {
          setErrorMessage(onValidate(newCode));
        }

        if (!isComplete) {
          focusElement(deRefAt(index - 1, inputsRef));
        }
      }
    },
    [code, isComplete, onValidate],
  );

  const handleBlur = useCallback(
    () => {
      if (isFn(onValidate)) {
        setErrorMessage(onValidate(code));
      }

      if (isFn(onBlur)) {
        onBlur(code);
      }
    },
    [code, onBlur, onValidate],
  );

  // on load focus the first element
  useEffect(() => void focusElement(deRefAt(0, inputsRef)), []);

  return (
    <div data-bem="CodeInput">
      {
        // NOTE: the issue is that label only works with a single input
        // so, it doesn't cover multiple of them embedded as children
      }
      {/* eslint-disable-next-line jsx-a11y/label-has-for */}
      <label data-bem="CodeInput__content">
        <span data-bem="CodeInput__label" className="field-name col-md-5">
          {label}
        </span>

        <div data-bem="CodeInput__inputs" className="verify-code">
          {asArray(code).map((digit, index) => {
            const key = `input-${index}`;
            const value = Number.isSafeInteger(digit) ? digit : '';
            return (
              <IndexedInput
                key={key}
                index={index}
                ref={setRefFor(index)}
                type={type}
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                value={value}
                tabIndex={0}
                className={cn({
                  'utl-full-width col-md-7': true,
                  'has-error': errorMessage,
                })}
                inputMode="decimal"
              />
            );
          })}
        </div>
      </label>

      <div className="input-baseline" />

      <div
        data-bem="CodeInput__error"
        className={cn({
          'input-prompt-show': errorMessage,
          'input-prompt-hide': !errorMessage,
        })}
      >
        <div className="input-baseline" />
        <span data-bem="CodeInput__error-message">{errorMessage}</span>
      </div>
    </div>
  );
};

// noinspection JSUnusedGlobalSymbols
export default OldCodeInput;
